// @flow

import * as React from 'react';
import { useQuery, type TypedDocumentNode, type CombinedError } from 'urql';
import { useDebouncedCallback, type DebounceOptions } from '@ta/common.throttle';

/**
 * NOTE: SSR is not supported for debounced queries.
 */
type UseDebouncedQueryArgs<T, V: {}> = {|
  query: string | TypedDocumentNode<T, V>,
  variables?: V,
  pause: boolean,
  wait: number,
  debounceOptions?: DebounceOptions,
|};

type UseDebouncedQueryResponse<T> = {
  data: ?T,
  error: ?CombinedError,
};

const proxy = func => func();

const useDebouncedQuery = <T, V: {}>({
  query,
  variables,
  pause,
  wait,
  debounceOptions,
}: UseDebouncedQueryArgs<T, V>): UseDebouncedQueryResponse<T> => {
  const [data, setData] = React.useState(undefined);
  const [error, setError] = React.useState(undefined);
  const [queryState, executeQuery] = useQuery({ query, variables, pause: true });

  // keep constant function reference to maintain debounced queue
  const execute = useDebouncedCallback(proxy, wait, debounceOptions);
  React.useEffect(() => {
    if (execute && !pause) {
      execute(executeQuery);
    }
  }, [execute, pause, executeQuery]);

  React.useEffect(() => {
    if (!queryState.fetching) {
      if (error !== queryState.error) {
        setError(queryState.error);
      }
      if (data !== queryState.data) {
        setData(queryState.data);
      }
    }
  }, [queryState, data, error]);

  return { data, error };
};

export default useDebouncedQuery;
